/**
 * When an activity row happened, as the person reading the feed sees it
 * (#11055).
 *
 * An audit row's timestamp is an **instant**, so the calendar day it falls on
 * genuinely depends on who is reading -- and the reader here is whoever has the
 * feed open. This module answers "which day?" and "what time?" once, so the day
 * a row is filed under, the day its header names and the clock printed beside
 * it can never be three different answers.
 *
 * They were two. The feed keyed each row on `getFullYear()` / `getMonth()` /
 * `getDate()` and printed its clock with a bare `toLocaleString`, all of which
 * read the **host browser's** zone -- while every other date in the RMS renders
 * in the zone the viewer *resolved*, which since #11055 they can pin to
 * something other than the browser's. Pin one and the feed carried on grouping
 * and stamping by the browser's days, so a row could sit under a header naming
 * a day it did not happen on for that reader.
 *
 * The zone the payload carries is not the answer either, though it is nearly
 * always the same string. It is the zone that was resolved when the *request*
 * was sent, and the feed does not refetch when a viewer re-pins, so reading it
 * would leave the previous zone's days on screen until the cache expired. The
 * reader's zone is asked for at render time, which is the only time it is
 * knowably current.
 */

import {
  calendarDaysBetween,
  formatCalendarDay,
  shortTimeZoneName,
  temporalDayIn,
  todayInZone,
  type Temporal,
} from "@/utils/temporal";

/** Rows that share a calendar day, as the viewer's zone divides them up. */
export interface ActivityDayGroup<T> {
  /** The bare `YYYY-MM-DD` these rows share, as seen from the viewer's zone. */
  day: string;
  entries: T[];
}

/**
 * Bucket rows into the viewer's days, keeping the order they arrived in.
 *
 * The feed is served newest-first and paginates by keyset, so insertion order
 * is already the order the days should read in; re-sorting here would only
 * invite it to disagree with the order rows appear inside a group.
 */
export const groupActivityByDay = <T>(
  entries: T[],
  occurrenceOf: (entry: T) => Temporal,
  viewerZone: string
): ActivityDayGroup<T>[] => {
  const byDay = new Map<string, T[]>();
  for (const entry of entries) {
    const day = temporalDayIn(occurrenceOf(entry), viewerZone);
    const existing = byDay.get(day);
    if (existing) existing.push(entry);
    else byDay.set(day, [entry]);
  }
  return [...byDay].map(([day, group]) => ({ day, entries: group }));
};

/**
 * The heading over a day's rows, relative to what "today" is for the viewer.
 *
 * "Today" and "Yesterday" are measured against the viewer's own today in the
 * same zone the rows were bucketed in -- a relative label resolved against a
 * different zone than the group it heads is how a header ends up contradicting
 * the rows underneath it. "Yesterday" is the calendar day before, by day
 * arithmetic rather than the instant 24 hours ago, which lands on the wrong
 * date across a daylight-saving boundary.
 *
 * `now` is injectable so the relative labels are testable without moving the
 * machine clock.
 */
export const activityDayHeading = (
  day: string,
  viewerZone: string,
  now: Date = new Date()
): string => {
  const elapsed = calendarDaysBetween(day, todayInZone(viewerZone, now));
  if (elapsed === 0) return "Today";
  if (elapsed === 1) return "Yesterday";
  return formatCalendarDay(day, "weekdayMedium");
};

/** How a row's moment reads on the feed. */
export interface ActivityTimeLabels {
  /** `5:07 PM` -- the clock beside the row. Empty when the row states no time. */
  clock: string;
  /** `Aug 4, 2026 5:07:32 PM EDT` -- the precise reading, on hover. */
  full: string;
}

/**
 * `at` is offset-bearing, so this is `Date` used for the one thing it models
 * correctly -- a point on the timeline -- and the zone its clock is read in is
 * named rather than left to the host, which is the whole fix.
 */
const clockIn = (at: string, viewerZone: string, seconds: boolean): string =>
  new Date(at).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" as const } : {}),
    timeZone: viewerZone,
  });

/**
 * The day, clock and zone a row shows, all read in the viewer's zone.
 *
 * The hover text names the zone because the viewer's may not be the browser's
 * any more: a clock with nothing beside it saying which clock it is has already
 * shipped as a bug here.
 *
 * The `date` arm is unreachable for an audit row -- every one of them is a
 * `timestamptz` -- and is handled rather than assumed away because
 * `granularity` is a tag and not a binary. A row that ever states only a day
 * gets no clock, instead of the fabricated midnight that reading a day as a
 * moment would print. The `never` arm keeps a coarser precision added later a
 * compile error here rather than a silent fallthrough.
 */
export const activityTimeLabels = (
  occurred: Temporal,
  viewerZone: string
): ActivityTimeLabels => {
  const day = formatCalendarDay(temporalDayIn(occurred, viewerZone), "medium");
  switch (occurred.granularity) {
    case "date":
      return { clock: "", full: day };
    case "instant": {
      const precise = `${day} ${clockIn(occurred.at, viewerZone, true)}`;
      const zoneName = shortTimeZoneName(occurred.at, viewerZone);
      return {
        clock: clockIn(occurred.at, viewerZone, false),
        full: zoneName ? `${precise} ${zoneName}` : precise,
      };
    }
    default: {
      const unhandled: never = occurred;
      throw new Error(
        `unhandled activity timestamp granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};
